import { useAppTranslation } from "@/i18n/useAppTranslation";
import { BRAND_GREEN } from "@/lib/design-tokens";
import welcomeCarImg from "@/assets/images/welcome-car.png";

interface HostWelcomeModalProps {
  open: boolean;
  onStart: () => void;
  onSkip: () => void;
  firstName?: string;
  // Task #4221 — the welcome modal now serves the Dashboard, Earnings and
  // Reviews tours. Callers override these keys to swap the headline/body copy;
  // they default to the original Dashboard tour keys so existing usage is
  // unchanged.
  titleKey?: string;
  bodyKey?: string;
}

export function HostWelcomeModal({
  open,
  onStart,
  onSkip,
  firstName,
  titleKey = "host_tour.welcome_title",
  bodyKey = "host_tour.welcome_body",
}: HostWelcomeModalProps) {
  const { t } = useAppTranslation();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 material-scrim animate-in fade-in-0 duration-300"
        onClick={onSkip}
      />

      <div className="relative z-[101] mx-4 w-full max-w-lg animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-300">
        <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="relative text-center">
            <div className="relative h-48 w-full overflow-hidden sm:h-56">
              <img
                src={welcomeCarImg}
                alt=""
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            </div>

            <div className="absolute inset-x-0 bottom-0 px-8 pb-5">
              <h2 className="font-sans text-2xl font-bold text-white drop-shadow-lg sm:text-3xl">
                {t(titleKey)}
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-white/90 drop-shadow sm:text-base">
                {t(bodyKey, { name: firstName || t("host_tour.welcome_name_fallback") })}
              </p>
            </div>
          </div>

          <div className="space-y-3 px-8 pb-8 pt-6">
            <button
              type="button"
              onClick={onStart}
              className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: BRAND_GREEN }}
              data-testid="tour-start-btn"
            >
              {t("host_tour.cta_start")}
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="cursor-pointer flex w-full items-center justify-center rounded-lg border border-gray-200 px-6 py-3 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
              data-testid="tour-skip-btn"
            >
              {t("host_tour.cta_skip")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
